import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, BarChart, Donut } from '../../components/ui/Charts';
import { Dialog } from '../../components/ui/Dialog';
import { useToast } from '../../components/ui/ToastProvider';
import { useApp } from '../../lib/app/AppContext';
import { revenue } from '../../data/seedData';

const licenses = [
  {
    name: 'Solo',
    price: 79,
    description: 'For independent builders shipping client or internal products.',
    features: ['1 developer', '3 commercial projects', 'All app pages', 'Lifetime updates'],
    featured: false,
  },
  {
    name: 'Studio',
    price: 149,
    description: 'For small teams and agencies building multiple products.',
    features: ['3 developers', '10 commercial projects', 'Priority support', 'Design source access'],
    featured: true,
  },
  {
    name: 'Extended',
    price: 299,
    description: 'For teams building SaaS products with broader redistribution rights.',
    features: ['10 developers', 'Unlimited projects', 'SaaS redistribution', '12 months priority support'],
    featured: false,
  },
] as const;

const featureCards = [
  ['01', 'Executive dashboards', 'Revenue, retention, delivery and operating health with responsive data views.'],
  ['02', 'Interactive analytics', 'Hoverable charts, selectable periods, funnels, cohorts and channel composition.'],
  ['03', 'Projects & delivery', 'Search, filters, CRUD flows, project details, environment state and health tracking.'],
  ['04', 'Real messaging', 'Persistent inbox, sent items, drafts, replies, starring, archiving and read state.'],
  ['05', 'Team operations', 'Profiles, workload, invitations, roles, status and operational visibility.'],
  ['06', 'Production foundations', 'Protected routes, local persistence, command palette, settings and responsive navigation.'],
] as const;

export function SalesPage() {
  const { pushToast } = useToast();
  const { user, addLicenseRequest, licenseRequests } = useApp();
  const [license, setLicense] = useState<(typeof licenses)[number] | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const demoPath = user ? '/dashboard' : '/login';

  const chartData = useMemo(
    () => revenue.map((item, index) => ({ ...item, secondary: 150 + index * 21 })),
    [],
  );

  function prepareLicenseRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!license) return;

    const form = new FormData(event.currentTarget);
    const rawName = form.get('name');
    const rawEmail = form.get('email');
    const rawCompany = form.get('company');

    const request = addLicenseRequest({
      license: license.name,
      priceUsd: license.price,
      name: typeof rawName === 'string' ? rawName.trim() : '',
      email: typeof rawEmail === 'string' ? rawEmail.trim().toLowerCase() : '',
      company: typeof rawCompany === 'string' ? rawCompany.trim() : '',
    });

    const blob = new Blob([JSON.stringify(request, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'gpt-build-license-request.json';
    anchor.click();
    URL.revokeObjectURL(url);

    setSubmitted(true);
    pushToast('License request saved', {
      message: license.name + ' · $' + license.price + ' USD',
      tone: 'success',
    });
  }

  return (
    <div className="sales-site">
      <header className="sales-nav">
        <a className="sales-brand" href="#top"><span>G</span><strong>GPT Build</strong></a>
        <nav aria-label="Sales navigation">
          <a href="#product">Product</a>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </nav>
        <div className="sales-nav-actions">
          <Link className="btn ghost" to="/login">Sign in</Link>
          <Link className="btn primary" to={demoPath}>Live preview</Link>
        </div>
      </header>

      <main id="top">
        <section className="sales-hero">
          <div className="sales-hero-copy">
            <span className="sales-eyebrow">React 19 · TypeScript · Vite · Local-first</span>
            <h1>Ship a polished SaaS control center without rebuilding the boring 80%.</h1>
            <p>
              A commercial-grade React application template with working dashboards, analytics,
              messaging, projects, teams, settings, protected routes and a complete responsive design system.
            </p>
            <div className="sales-hero-actions">
              <Link className="btn primary xl" to={demoPath}>Explore the live app →</Link>
              <a className="btn secondary xl" href="#pricing">View licenses</a>
            </div>
            <div className="sales-proof-row">
              <span><b>7+</b> product surfaces</span>
              <span><b>60+</b> working interactions</span>
              <span><b>0</b> backend required</span>
            </div>
          </div>

          <div className="sales-product-stage" aria-label="Interactive dashboard preview">
            <div className="stage-topbar"><i /><i /><i /><span>Revenue command center</span></div>
            <div className="stage-grid">
              <article className="stage-stat"><span>MRR</span><strong>$428.6K</strong><b>+18.4%</b></article>
              <article className="stage-stat"><span>NRR</span><strong>121.8%</strong><b>+3.2%</b></article>
              <article className="stage-stat"><span>Active teams</span><strong>12,842</strong><b>+9.7%</b></article>
            </div>
            <div className="stage-chart-card">
              <div><strong>Growth trajectory</strong><span>Hover the graph</span></div>
              <AreaChart data={chartData} />
            </div>
          </div>
        </section>

        <section className="sales-trust">
          <span>Built for founders</span>
          <span>Product teams</span>
          <span>Agencies</span>
          <span>Internal tools</span>
          <span>SaaS products</span>
        </section>

        <section className="sales-section" id="product">
          <div className="sales-section-head">
            <span className="sales-eyebrow">A real application, not a screenshot kit</span>
            <h2>Every major screen is wired and usable.</h2>
            <p>The template is designed to be evaluated by clicking through it—not by imagining what unfinished controls might do.</p>
          </div>
          <div className="sales-showcase-grid">
            <article className="sales-showcase large">
              <div className="sales-showcase-copy">
                <span>Analytics</span>
                <h3>Graphs that communicate, not decorate.</h3>
                <p>Interactive hover states, responsive scaling, readable comparisons and accessible summaries.</p>
              </div>
              <BarChart data={revenue} />
            </article>

            <article className="sales-showcase">
              <div className="sales-showcase-copy"><span>Health</span><h3>Dense information, controlled hierarchy.</h3></div>
              <div className="sales-donut-row">
                <Donut value={97} label="healthy" sublabel="Service health" />
                <Donut value={91} label="SLO" sublabel="30-day target" />
              </div>
            </article>

            <article className="sales-showcase">
              <div className="sales-showcase-copy"><span>Messaging</span><h3>Inbox patterns ready for real product work.</h3></div>
              <div className="sales-message-preview">
                <div><b>PS</b><span><strong>Q4 launch readiness</strong><small>One analytics dependency remains.</small></span><i>Now</i></div>
                <div><b>MP</b><span><strong>Design system review</strong><small>Accessibility pass is complete.</small></span><i>8m</i></div>
                <div><b>EB</b><span><strong>Latency resolved</strong><small>P95 is back below target.</small></span><i>1h</i></div>
              </div>
            </article>
          </div>
        </section>

        <section className="sales-section" id="features">
          <div className="sales-section-head split">
            <div>
              <span className="sales-eyebrow">Commercial template depth</span>
              <h2>Enough product surface to start from, not fight against.</h2>
            </div>
            <p>
              Premium admin templates compete on breadth—multiple dashboards, app pages, chart systems
              and complete UI kits—not one attractive overview screen.
            </p>
          </div>
          <div className="sales-feature-grid">
            {featureCards.map(([number, title, copy]) => (
              <article key={title}><span>{number}</span><h3>{title}</h3><p>{copy}</p></article>
            ))}
          </div>
        </section>

        <section className="sales-section sales-pricing-section" id="pricing">
          <div className="sales-section-head">
            <span className="sales-eyebrow">One-time commercial licenses</span>
            <h2>Choose the rights you need. Keep the code.</h2>
            <p>No recurring template subscription. Upgrade when your team or distribution model grows.</p>
          </div>
          <div className="pricing-grid">
            {licenses.map((item) => (
              <article className={item.featured ? 'pricing-card featured' : 'pricing-card'} key={item.name}>
                {item.featured ? <span className="pricing-popular">Most popular</span> : null}
                <span className="pricing-name">{item.name}</span>
                <div className="pricing-price"><strong>{'$'}{item.price}</strong><span>USD · one time</span></div>
                <p>{item.description}</p>
                <ul>{item.features.map((feature) => <li key={feature}>✓ {feature}</li>)}</ul>
                <button
                  className={item.featured ? 'btn primary full' : 'btn secondary full'}
                  onClick={() => {
                    setLicense(item);
                    setSubmitted(false);
                  }}
                >
                  Request {item.name}
                </button>
              </article>
            ))}
          </div>
        </section>

        <section className="sales-section sales-faq" id="faq">
          <div className="sales-section-head">
            <span className="sales-eyebrow">FAQ</span>
            <h2>Commercially practical by design.</h2>
          </div>
          <div className="faq-grid">
            <details open><summary>Is this just UI?</summary><p>No. Navigation, local authentication, persistent state, CRUD workflows, messaging, settings and data interactions are implemented.</p></details>
            <details><summary>Can I connect my own backend?</summary><p>Yes. The application state layer is isolated so local persistence can be replaced by REST, GraphQL, Supabase, Firebase or your own API.</p></details>
            <details><summary>Does it support commercial projects?</summary><p>The pricing tiers are structured around developer seats, project counts and redistribution scope so buyers can choose the appropriate commercial license.</p></details>
            <details><summary>Do I own the generated product?</summary><p>The template is designed as source code you customize and deploy. Your product data and implementation remain under your control.</p></details>
          </div>
        </section>

        <section className="sales-final-cta">
          <span className="sales-eyebrow">Start from a complete operating surface</span>
          <h2>Build the unique part of your product instead.</h2>
          <div>
            <Link className="btn primary xl" to={demoPath}>Open live preview</Link>
            <a className="btn secondary xl" href="#pricing">Compare licenses</a>
          </div>
        </section>
      </main>

      <footer className="sales-footer">
        <div>
          <span className="sales-brand"><span>G</span><strong>GPT Build</strong></span>
          <p>Commercial React application template for modern SaaS products.</p>
        </div>
        <div>
          <a href="#product">Product</a>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <Link to="/login">Sign in</Link>
        </div>
      </footer>

      <Dialog
        open={Boolean(license)}
        title={license ? 'Request ' + license.name + ' license' : 'Request license'}
        onClose={() => setLicense(null)}
      >
        {submitted ? (
          <div className="purchase-success">
            <strong>License request saved.</strong>
            <p>
              The request is persisted locally and downloaded as a portable JSON record.
              Prepared requests: {licenseRequests.length}.
            </p>
            <button className="btn primary" onClick={() => setLicense(null)}>Done</button>
          </div>
        ) : (
          <form className="form-stack" onSubmit={prepareLicenseRequest}>
            <p className="dialog-copy">
              Prepare a portable purchase request for the <b>{license?.name}</b> license
              at <b>{'$'}{license?.price} USD</b>.
            </p>
            <label>Name<input name="name" required /></label>
            <label>Email<input name="email" type="email" required /></label>
            <label>Company<input name="company" /></label>
            <button className="btn primary" type="submit">Prepare license request</button>
          </form>
        )}
      </Dialog>
    </div>
  );
}
